import React, { useEffect, useMemo, useRef, useState } from "react";
import theme from "../../styles/theme";
import drawSpaceConfig from "./config/drawSpaceConfig";

import useDrawSpaceData from "./hooks/useDrawSpaceData";
import useDrawSpaceEditor from "./hooks/useDrawSpaceEditor";
import useDrawSpaceView from "./hooks/useDrawSpaceView";

import DrawToolbar from "./components/DrawToolbar";
import DrawViewport from "./components/DrawViewport";
import DrawPropertiesPanel from "./components/DrawPropertiesPanel";
import DrawSiteLevelSidebar from "./components/DrawSiteLevelSidebar";

import { printSpatialView } from "./utils/drawPrint";

/*
====================================================================
HELPERS
====================================================================
*/

const geometryToDrawableEntity = ({
  id,
  label,
  geometry,
  paletteKey,
  interactive = false,
}) => {
  if (!geometry) return null;

  if (geometry.kind === "RECT") {
    return {
      id,
      label,
      type: drawSpaceConfig.entityTypes.AREA_RECT,
      geometry: {
        x: geometry.x,
        y: geometry.y,
        width: geometry.width,
        height: geometry.height,
      },
      paletteKey,
      interactive,
    };
  }

  if (geometry.kind === "POLYLINE") {
    return {
      id,
      label,
      type: drawSpaceConfig.entityTypes.AREA_POLYLINE,
      geometry: {
        points: geometry.points ?? [],
      },
      paletteKey,
      interactive,
    };
  }

  return null;
};

const structureToPropertiesEntity = (mode, activeOuvrage, activeLevel) => {
  if (mode === "SITE" && activeOuvrage) {
    const geometry = activeOuvrage.footprintGeometry ?? {};

    if (geometry.kind === "RECT") {
      return {
        id: activeOuvrage.id,
        type: "OUVRAGE",
        label: activeOuvrage.label,
        geometry: {
          x: geometry.x ?? "",
          y: geometry.y ?? "",
          width: geometry.width ?? "",
          height: geometry.height ?? "",
        },
        attributes: {
          elevation: "",
        },
      };
    }

    if (geometry.kind === "POLYLINE") {
      return {
        id: activeOuvrage.id,
        type: "OUVRAGE",
        label: activeOuvrage.label,
        geometry: {
          points: geometry.points ?? [],
        },
        attributes: {
          elevation: "",
        },
      };
    }
  }

  if (mode === "OUVRAGE" && activeLevel) {
    const geometry = activeLevel.outlineGeometry ?? {};

    if (geometry.kind === "RECT") {
      return {
        id: activeLevel.id,
        type: "LEVEL",
        label: activeLevel.label,
        geometry: {
          x: geometry.x ?? "",
          y: geometry.y ?? "",
          width: geometry.width ?? "",
          height: geometry.height ?? "",
        },
        attributes: {
          elevation: activeLevel.elevation ?? "",
        },
      };
    }

    if (geometry.kind === "POLYLINE") {
      return {
        id: activeLevel.id,
        type: "LEVEL",
        label: activeLevel.label,
        geometry: {
          points: geometry.points ?? [],
        },
        attributes: {
          elevation: activeLevel.elevation ?? "",
        },
      };
    }
  }

  return null;
};

const propertiesEntityToStructurePatch = (mode, entity) => {
  if (!entity) return null;

  if (mode === "SITE") {
    const hasRect =
      entity.geometry &&
      "x" in entity.geometry &&
      "y" in entity.geometry &&
      "width" in entity.geometry &&
      "height" in entity.geometry;

    const geometry = hasRect
      ? {
          kind: "RECT",
          x: Number(entity.geometry.x),
          y: Number(entity.geometry.y),
          width: Number(entity.geometry.width),
          height: Number(entity.geometry.height),
        }
      : {
          kind: "POLYLINE",
          points: entity.geometry?.points ?? [],
        };

    return {
      label: entity.label,
      footprintGeometry: geometry,
    };
  }

  if (mode === "OUVRAGE") {
    const hasRect =
      entity.geometry &&
      "x" in entity.geometry &&
      "y" in entity.geometry &&
      "width" in entity.geometry &&
      "height" in entity.geometry;

    const geometry = hasRect
      ? {
          kind: "RECT",
          x: Number(entity.geometry.x),
          y: Number(entity.geometry.y),
          width: Number(entity.geometry.width),
          height: Number(entity.geometry.height),
        }
      : {
          kind: "POLYLINE",
          points: entity.geometry?.points ?? [],
        };

    return {
      label: entity.label,
      elevation:
        entity.attributes?.elevation === ""
          ? null
          : Number(entity.attributes?.elevation),
      outlineGeometry: geometry,
    };
  }

  return null;
};

/*
====================================================================
COMPOSANT PRINCIPAL
====================================================================
*/

const DrawSpace25D = ({
  projectId,
  assetId = null,

  width = drawSpaceConfig.defaults.width,
  height = drawSpaceConfig.defaults.height,

  assetLabel = drawSpaceConfig.defaults.assetLabel,

  showToolbar = drawSpaceConfig.defaults.showToolbar,
  showPropertiesPanel = drawSpaceConfig.defaults.showPropertiesPanel,
  showGrid = drawSpaceConfig.defaults.showGrid,

  enablePersistence = drawSpaceConfig.defaults.enablePersistence,
  enablePrint = drawSpaceConfig.defaults.enablePrint,

  readOnlyMode = drawSpaceConfig.defaults.readOnlyMode,
  disabled = drawSpaceConfig.defaults.disabled,

  printTitle = drawSpaceConfig.defaults.printTitle,
  printOrientation = drawSpaceConfig.defaults.printOrientation,

  editedEntity = null,

  onChange,
  onSelectionChange,
  onDataLoaded,

  style = {},
  toolbarStyle = {},
  viewportStyle = {},
  panelStyle = {},
}) => {
  const viewportHostRef = useRef(null);

  const [gridSize, setGridSize] = useState(drawSpaceConfig.defaults.gridSize);
  const [snapToGrid, setSnapToGrid] = useState(
    drawSpaceConfig.defaults.defaultSnapToGrid
  );

  const [warningMessage, setWarningMessage] = useState(null);
  const [activeMode, setActiveMode] = useState("SITE");

  /*
  ================================================================
  DONNÉES SPATIALES
  ================================================================
  */

  const {
    sites,

    activeSiteId,
    setActiveSiteId,

    activeOuvrageId,
    setActiveOuvrageId,

    activeLevelId,
    setActiveLevelId,

    activeSite,
    activeOuvrage,
    activeLevel,

    getActiveEntities,
    updateEntitiesForActiveLevel,

    updateOuvrageById,
    updateLevelById,

    createSite,
    deleteSite,

    createOuvrage,
    createOuvrageFromGeometry,
    deleteOuvrage,

    createLevel,
    createLevelFromGeometry,
    deleteLevel,

    loading,
  } = useDrawSpaceData({
    projectId,
    assetId,
    enablePersistence,
  });

  /*
  ================================================================
  GESTION DE LA VUE
  ================================================================
  */

  const {
    zoom,
    offsetX,
    offsetY,
    zoomIn,
    zoomOut,
    resetView,
    pan,
    zoomAtPoint,
    screenToWorld,
  } = useDrawSpaceView();

  /*
  ================================================================
  ENTITÉS ACTIVES
  ================================================================
  */

  const activeEntities = getActiveEntities();

  /*
  ================================================================
  ÉDITEUR DES AREAS
  ================================================================
  */

  const {
    tool,
    setTool,

    selectedId,
    selectEntity,
    clearSelection,

    replaceEntity,

    createRectangle,
    createCircle,
    createLine,
    createPolyline,

    moveEntity,
    deleteEntity,
    copyEntity,
  } = useDrawSpaceEditor({
    activeFloorId: activeLevelId,
    entities: activeEntities,
    updateEntities: (_, nextEntities) =>
      updateEntitiesForActiveLevel(nextEntities),
  });

  /*
  ================================================================
  ROUTAGE DE CRÉATION
  ================================================================
  */

  const handleCreateRectangle = ({ x, y, width, height }) => {
    const geometry = {
      kind: "RECT",
      x,
      y,
      width,
      height,
    };

    if (activeMode === "SITE") {
      createOuvrageFromGeometry(geometry);
      setWarningMessage("Nouvel ouvrage créé à partir du contour dessiné.");
      return;
    }

    if (activeMode === "OUVRAGE") {
      createLevelFromGeometry(geometry);
      setWarningMessage("Nouveau niveau créé à partir du contour dessiné.");
      return;
    }

    createRectangle({ x, y, width, height });
  };

  const handleCreatePolyline = (points) => {
    const geometry = {
      kind: "POLYLINE",
      points,
    };

    if (activeMode === "SITE") {
      createOuvrageFromGeometry(geometry);
      setWarningMessage("Nouvel ouvrage créé à partir du contour dessiné.");
      return;
    }

    if (activeMode === "OUVRAGE") {
      createLevelFromGeometry(geometry);
      setWarningMessage("Nouveau niveau créé à partir du contour dessiné.");
      return;
    }

    createPolyline(points);
  };

  const handleCreateCircle = ({ cx, cy, r }) => {
    if (activeMode !== "NIVEAU") {
      setWarningMessage(
        "Le cercle n’est autorisé qu’en mode Level pour créer une Area."
      );
      return;
    }

    createCircle({ cx, cy, r });
  };

  const handleCreateLine = ({ x1, y1, x2, y2 }) => {
    if (activeMode !== "NIVEAU") {
      setWarningMessage(
        "La ligne n’est autorisée qu’en mode Level pour créer une Area."
      );
      return;
    }

    createLine({ x1, y1, x2, y2 });
  };

  /*
  ================================================================
  SÉLECTION / PROPRIÉTÉS
  ================================================================
  */

  const selectedAreaEntity = useMemo(() => {
    return activeEntities.find((entity) => entity.id === selectedId) ?? null;
  }, [activeEntities, selectedId]);

  const activeFloor = activeLevel ?? null;

  const propertiesEntity = useMemo(() => {
    if (activeMode === "NIVEAU") {
      return selectedAreaEntity ?? null;
    }

    return structureToPropertiesEntity(activeMode, activeOuvrage, activeLevel);
  }, [activeMode, selectedAreaEntity, activeOuvrage, activeLevel]);

  /*
  ================================================================
  VIEWPORT ENTITIES
  ================================================================
  */

  const viewportEntities = useMemo(() => {
    if (!activeSite) return [];

    if (activeMode === "SITE") {
      return (activeSite.ouvrages ?? [])
        .map((ouvrage) =>
          geometryToDrawableEntity({
            id: ouvrage.id,
            label: ouvrage.label,
            geometry: ouvrage.footprintGeometry,
            paletteKey: "ouvrage",
            interactive: false,
          })
        )
        .filter(Boolean);
    }

    if (activeMode === "OUVRAGE") {
      const items = [];

      if (activeOuvrage?.footprintGeometry) {
        items.push(
          geometryToDrawableEntity({
            id: `${activeOuvrage.id}_footprint`,
            label: activeOuvrage.label,
            geometry: activeOuvrage.footprintGeometry,
            paletteKey: "ouvrage",
            interactive: false,
          })
        );
      }

      (activeOuvrage?.levels ?? []).forEach((level) => {
        if (!level.outlineGeometry) return;

        items.push(
          geometryToDrawableEntity({
            id: level.id,
            label: level.label,
            geometry: level.outlineGeometry,
            paletteKey: "level",
            interactive: false,
          })
        );
      });

      return items.filter(Boolean);
    }

    if (activeMode === "NIVEAU") {
      const items = [];

      if (activeOuvrage?.footprintGeometry) {
        items.push(
          geometryToDrawableEntity({
            id: `${activeOuvrage.id}_footprint`,
            label: activeOuvrage.label,
            geometry: activeOuvrage.footprintGeometry,
            paletteKey: "ouvrage",
            interactive: false,
          })
        );
      }

      if (activeLevel?.outlineGeometry) {
        items.push(
          geometryToDrawableEntity({
            id: `${activeLevel.id}_outline`,
            label: activeLevel.label,
            geometry: activeLevel.outlineGeometry,
            paletteKey: "level",
            interactive: false,
          })
        );
      }

      return [
        ...items.filter(Boolean),
        ...activeEntities.map((entity) => ({
          ...entity,
          paletteKey: "area",
          interactive: true,
        })),
      ];
    }

    return [];
  }, [activeMode, activeSite, activeOuvrage, activeLevel, activeEntities]);

  const viewportSelectedId = activeMode === "NIVEAU" ? selectedId : null;

  /*
  ================================================================
  SYNCHRONISATION PANNEAU PROPRIÉTÉS
  ================================================================
  */

  useEffect(() => {
    if (!editedEntity || !propertiesEntity) return;
    if (editedEntity.id !== propertiesEntity.id) return;

    if (activeMode === "NIVEAU") {
      replaceEntity(editedEntity);
      return;
    }

    const patch = propertiesEntityToStructurePatch(activeMode, editedEntity);
    if (!patch) return;

    if (activeMode === "SITE" && activeOuvrage) {
      updateOuvrageById(activeOuvrage.id, patch);
    }

    if (activeMode === "OUVRAGE" && activeLevel) {
      updateLevelById(activeLevel.id, patch);
    }
  }, [
    editedEntity,
    propertiesEntity,
    activeMode,
    activeOuvrage,
    activeLevel,
    replaceEntity,
    updateOuvrageById,
    updateLevelById,
  ]);

  /*
  ================================================================
  CALLBACKS EXTERNES
  ================================================================
  */

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(propertiesEntity ?? null);
    }
  }, [propertiesEntity, onSelectionChange]);

  useEffect(() => {
    if (onChange) {
      onChange({
        projectId,
        assetId,

        sites,

        activeSiteId,
        activeOuvrageId,
        activeLevelId,

        activeSite,
        activeOuvrage,
        activeLevel,

        activeMode,
      });
    }
  }, [
    projectId,
    assetId,
    sites,
    activeSiteId,
    activeOuvrageId,
    activeLevelId,
    activeSite,
    activeOuvrage,
    activeLevel,
    activeMode,
    onChange,
  ]);

  useEffect(() => {
    if (onDataLoaded && !loading) {
      onDataLoaded({
        sites,
        activeSiteId,
        activeOuvrageId,
        activeLevelId,
        activeSite,
        activeOuvrage,
        activeLevel,
        activeMode,
      });
    }
  }, [
    loading,
    sites,
    activeSiteId,
    activeOuvrageId,
    activeLevelId,
    activeSite,
    activeOuvrage,
    activeLevel,
    activeMode,
    onDataLoaded,
  ]);

  useEffect(() => {
    setWarningMessage(null);
  }, [activeSiteId, activeOuvrageId, activeLevelId]);

  /*
  ================================================================
  CHANGEMENT D’OUTIL
  ================================================================
  */

  const handleToolChange = (nextTool) => {
    if (disabled || readOnlyMode) return;

    if (nextTool === drawSpaceConfig.tools.DELETE) {
      if (activeMode === "NIVEAU" && selectedId) {
        deleteEntity(selectedId);
      }
      return;
    }

    if (nextTool === drawSpaceConfig.tools.COPY) {
      if (activeMode === "NIVEAU" && selectedId) {
        copyEntity(selectedId);
      }
      return;
    }

    const isCreationTool =
      nextTool === drawSpaceConfig.tools.AREA_RECT ||
      nextTool === drawSpaceConfig.tools.AREA_POLYLINE ||
      nextTool === drawSpaceConfig.tools.LINE ||
      nextTool === drawSpaceConfig.tools.CIRCLE ||
      nextTool === drawSpaceConfig.tools.ARC;

    const isNavigationTool =
      nextTool === drawSpaceConfig.tools.PAN ||
      nextTool === drawSpaceConfig.tools.SELECT ||
      nextTool === drawSpaceConfig.tools.MOVE;

    if (isCreationTool || isNavigationTool) {
      clearSelection();
    }

    setTool(nextTool);
  };

  /*
  ================================================================
  IMPRESSION
  ================================================================
  */

  const handlePrint = () => {
    if (!enablePrint) return;
    if (!viewportHostRef.current) return;

    const svgElement = viewportHostRef.current.querySelector("svg");
    if (!svgElement) return;

    printSpatialView({
      svgElement,
      title: printTitle,
      assetLabel,
      floorLabel: activeFloor?.label,
      elevation: activeFloor?.elevation,
      orientation: printOrientation,
    });
  };

  /*
  ================================================================
  MISE À JOUR ENTITÉ / STRUCTURE
  ================================================================
  */

  const handleEntityChange = (updatedEntity) => {
    if (!updatedEntity) return;

    if (activeMode === "NIVEAU") {
      replaceEntity(updatedEntity);
      return;
    }

    const patch = propertiesEntityToStructurePatch(activeMode, updatedEntity);
    if (!patch) return;

    if (activeMode === "SITE" && activeOuvrage) {
      updateOuvrageById(activeOuvrage.id, patch);
    }

    if (activeMode === "OUVRAGE" && activeLevel) {
      updateLevelById(activeLevel.id, patch);
    }
  };

  /*
  ================================================================
  ÉTATS D’AFFICHAGE
  ================================================================
  */

  const blockedState = !projectId;

  const emptyState = useMemo(() => {
    if (loading || !projectId || !activeSite) return true;

    if (activeMode === "SITE") return false;
    if (activeMode === "OUVRAGE") return !activeOuvrage;
    if (activeMode === "NIVEAU") return !activeOuvrage || !activeLevel;

    return false;
  }, [loading, projectId, activeSite, activeOuvrage, activeLevel, activeMode]);

  /*
  ================================================================
  RENDER
  ================================================================
  */

  return (
    <div
      style={{
        width,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.colors.borders.panel}`,
        borderRadius: theme.borders.radius.lg,
        overflow: "hidden",
        background: theme.colors.background.surface,
        boxShadow: theme.shadows.xs,
        ...style,
      }}
    >
      {showToolbar && (
        <DrawToolbar
          tool={tool}
          setTool={handleToolChange}
          zoomIn={zoomIn}
          zoomOut={zoomOut}
          resetView={resetView}
          onPrint={handlePrint}
          enablePrint={enablePrint}
          gridSize={gridSize}
          setGridSize={setGridSize}
          snapToGrid={snapToGrid}
          setSnapToGrid={setSnapToGrid}
          style={toolbarStyle}
        />
      )}

      <div
        style={{
          display: "flex",
          width: "100%",
          minHeight: height,
          background: theme.colors.background.surface,
        }}
      >
        <DrawSiteLevelSidebar
          sites={sites}
          activeSiteId={activeSiteId}
          activeOuvrageId={activeOuvrageId}
          activeLevelId={activeLevelId}
          activeMode={activeMode}
          setActiveMode={setActiveMode}
          setActiveSiteId={setActiveSiteId}
          setActiveOuvrageId={setActiveOuvrageId}
          setActiveLevelId={setActiveLevelId}
          createSite={createSite}
          deleteSite={deleteSite}
          createOuvrage={createOuvrage}
          deleteOuvrage={deleteOuvrage}
          createLevel={createLevel}
          deleteLevel={deleteLevel}
        />

        <div
          ref={viewportHostRef}
          style={{
            flex: 1,
            minWidth: 0,
            position: "relative",
            background: theme.colors.background.panel,
          }}
        >
          {blockedState ? (
            <div
              style={{
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.sm,
                color: theme.colors.text.small,
                background: theme.colors.background.panel,
                ...viewportStyle,
              }}
            >
              No project selected
            </div>
          ) : emptyState ? (
            <div
              style={{
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.sm,
                color: theme.colors.text.small,
                background: theme.colors.background.panel,
                ...viewportStyle,
              }}
            >
              {drawSpaceConfig.labels.empty}
            </div>
          ) : (
            <DrawViewport
              width="100%"
              height={height}
              entities={viewportEntities}
              selectedId={viewportSelectedId}
              zoom={zoom}
              offsetX={offsetX}
              offsetY={offsetY}
              showGrid={showGrid}
              gridSize={gridSize}
              snapToGrid={snapToGrid}
              screenToWorld={screenToWorld}
              zoomAtPoint={zoomAtPoint}
              tool={tool}
              setTool={setTool}
              selectEntity={selectEntity}
              clearSelection={clearSelection}
              createRectangle={handleCreateRectangle}
              createCircle={handleCreateCircle}
              createLine={handleCreateLine}
              createPolyline={handleCreatePolyline}
              moveEntity={moveEntity}
              panViewport={pan}
              activeMode={activeMode}
              warningMessage={warningMessage}
              style={viewportStyle}
            />
          )}
        </div>

        {showPropertiesPanel && (
          <DrawPropertiesPanel
            entity={propertiesEntity}
            activeMode={activeMode}
            onChange={handleEntityChange}
            style={panelStyle}
          />
        )}
      </div>
    </div>
  );
};

export default DrawSpace25D;